/**
 * TableView — renders a polished data table inline in chat messages.
 *
 * Supports column types: text, person, status, date, number, priority, progress.
 * Person cells show a coloured avatar circle with initials.
 * Status and priority cells render a dot + pill badge.
 * Numbers are right-aligned with locale thousands separators.
 * Dates (ISO YYYY-MM-DD) are formatted as "DD MMM YYYY".
 * Progress shows a percentage with an optional inline bar.
 *
 * Defensive: slices data to maxRows (default 30, cap 50), shows a truncation
 * note when clipped, and handles missing row keys gracefully.
 */

import type {
	TableChartConfig,
	TableColumn,
	TableColumnType,
	TableStatusMapping,
} from "../../types/index.js";

import {
	formatDate,
	PRIORITY_COLOURS,
	resolveStatusColour,
} from "./chartUtils.js";

import {
	PersonCell,
	StatusBadge,
	ProgressCell,
} from "./chartShared.js";

interface TableViewProps {
	config: TableChartConfig;
	isModal?: boolean;
}

// ---------------------------------------------------------------------------
// Cell dispatcher
// ---------------------------------------------------------------------------

function renderCell(
	col: TableColumn,
	rawValue: string | number | null | undefined,
	mappings: TableStatusMapping[] | undefined,
) {
	const type: TableColumnType = col.type ?? "text";
	const strValue = rawValue === null || rawValue === undefined ? "" : String(rawValue);

	switch (type) {
		case "person":
			return strValue ? <PersonCell value={strValue} /> : null;

		case "status": {
			if (!strValue) return null;
			const colourKey = resolveStatusColour(strValue, mappings);
			return <StatusBadge colourKey={colourKey} label={strValue} />;
		}

		case "priority": {
			if (!strValue) return null;
			const colourKey = PRIORITY_COLOURS[strValue] ?? "grey";
			return <StatusBadge colourKey={colourKey} label={strValue} />;
		}

		case "date":
			return strValue ? <>{formatDate(strValue)}</> : null;

		case "number": {
			const num = Number(rawValue);
			if (rawValue === null || rawValue === undefined || rawValue === "" || isNaN(num)) return null;
			return <>{num.toLocaleString()}</>;
		}

		case "progress": {
			const num = Number(rawValue);
			if (rawValue === null || rawValue === undefined || rawValue === "" || isNaN(num)) return null;
			return <ProgressCell value={num} />;
		}

		case "text":
		default:
			return <>{strValue}</>;
	}
}

/** Resolve text alignment for a column. Numbers and progress default to right; others default to left. */
function resolveAlign(col: TableColumn): "left" | "center" | "right" {
	if (col.align) return col.align;
	const type = col.type ?? "text";
	return type === "number" || type === "progress" ? "right" : "left";
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DEFAULT_MAX_ROWS = 30;
const ABSOLUTE_CAP = 50;

export default function TableView({ config, isModal: _isModal = false }: TableViewProps) {
	const { columns, data, statusMappings, maxRows } = config;

	// Resolve effective row limit
	const limit = Math.min(maxRows ?? DEFAULT_MAX_ROWS, ABSOLUTE_CAP);
	const totalRows = data?.length ?? 0;
	const displayData = (data ?? []).slice(0, limit);
	const isTruncated = totalRows > limit;

	// Empty state
	if (totalRows === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div className="table-view-wrapper">
			<div className="table-view-scroll">
				<table className="table-view-table" aria-label="Data table">
					<thead>
						<tr>
							{columns.map((col) => (
								<th
									key={col.key}
									className="table-view-th"
									scope="col"
									style={{ textAlign: resolveAlign(col) }}
								>
									{col.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{displayData.map((row, rowIdx) => (
							<tr key={rowIdx} className="table-view-row">
								{columns.map((col) => {
									const rawValue = row[col.key];
									const align = resolveAlign(col);
									return (
										<td
											key={col.key}
											className="table-view-td"
											style={{ textAlign: align }}
										>
											{renderCell(col, rawValue, statusMappings)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{isTruncated && (
				<p className="table-view-truncation-note" role="note">
					Showing {limit} of {totalRows} rows
				</p>
			)}
		</div>
	);
}
